import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/clientApp';
import { useUser } from './useUser';
import {
  collection,
  orderBy,
  limit,
  query,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';

const ContactsContext = React.createContext();

function ContactsProvider({ children }) {
  const { user } = useUser();
  const [contacts, setContacts] = useState([]); // get a list of the users recent contacts which we can use to populate the sharing modal
  // const [lastVisible, setLastVisible] = useState(null);
  // const [loadedAllContacts, setLoadedAllContacts] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;

    const contactsQuery = query(
      collection(db, `users/${user.uid}/contacts`),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const subscribeToContacts = onSnapshot(
      contactsQuery,
      (snapshot) => {
        // const lastVisible = last(snapshot.docs);
        // setLastVisible(lastVisible);
        const data = snapshot.docs.map((doc) => {
          return { ...doc.data(), id: doc.id };
        });
        if (data.length) {
          setContacts(data);
        }
      },
      (error) => {
        console.error('Error loading contacts: ', error);
      }
    );

    return () => subscribeToContacts();
  }, [user]);

  //we will need to paginate later, but for now we will just load all contacts since we need to set up typesense search etc first

  // const loadMoreContacts = async (uid) => {
  //   if (!uid || !lastVisible) {
  //     console.error('Error loading more contacts: ', uid, lastVisible);
  //     return;
  //   }

  //   const contactsQuery = query(
  //     collection(db, `users/${uid}/contacts`),
  //     orderBy('createdAt', 'desc'),
  //     startAfter(lastVisible),
  //     limit(20)
  //   );

  //   const snapshot = await getDocs(contactsQuery);
  //   const last = snapshot.docs[snapshot.docs.length - 1];
  //   setLastVisible(last);
  //   const data = snapshot.docs.map((doc) => {
  //     return { ...doc.data(), id: doc.id };
  //   });
  //   if (data.length) {
  //     setContacts((contacts) => [...contacts, ...data]);
  //   }
  //   if (data.length < 20) {
  //     setLoadedAllContacts(true);
  //   }
  // };

  const addContact = async (contact, uid) => {
    if (!contact || !uid) {
      console.error('Error adding contact: ', contact, uid);
      return;
    }
    try {
      const docRef = await addDoc(collection(db, `users/${uid}/contacts`), {
        ...contact,
        createdAt: Date.now(),
      });
      console.log('Contact added with ID: ', docRef.id);
    } catch (error) {
      console.error('Error adding contact: ', error);
    }
  };

  const editContact = async (contactId, contact, uid) => {
    if (!contactId || !contact || !uid) {
      console.error('Error editing contact: ', contactId, contact, uid);
      return;
    }

    try {
      const docRef = doc(db, `users/${uid}/contacts`, contactId);
      await updateDoc(docRef, contact);
    } catch (error) {
      console.error('Error editing contact: ', error);
    }
  };

  const deleteContact = async (contactId, uid) => {
    if (!contactId || !uid) {
      console.error('Error deleting contact: ', contactId, uid);
      return;
    }

    try {
      const docRef = doc(db, `users/${uid}/contacts`, contactId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting contact: ', error);
    }
  };

  const value = useMemo(
    () => ({
      contacts,
      setContacts,
      addContact,
      deleteContact,
      editContact,
    }),
    [contacts]
  );
  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
}

function useContacts() {
  const context = React.useContext(ContactsContext);
  if (context === undefined) {
    throw new Error('useContacts must be used within a ContactsProvider');
  }
  return context;
}

export { ContactsProvider, useContacts };
